"use client";

import { cn } from "@punchup/utils/cn";
import { formatRuntime } from "@punchup/utils/formatRuntime";
import { rgbDataURL } from "@punchup/utils/rgbDataURL";

import ComedianVideoLivestreamBadge from "@/components/comedianPage/v2/ComedianVideoLivestreamBadge";
import Image from "next/image";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import LockIcon from "@/components/common/icons/LockIcon";
import { Play } from "lucide-react";
import { SpecialRow } from "@/data/specials";
import { WatchCTAButton } from "@/components/special";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@/data/images";
import { useSpecialUnlockedForUser } from "@/hooks/specials/useSpecialUnlockedForUser";
import { useUser } from "@punchup/data/legacy/hooks";

interface ComedianVideoThumbnailProps {
  special: SpecialRow;
  displayFeatured?: boolean;
  className?: string;
  borderRadius?: number;
  withPlayIcon?: boolean;
  /**
   * Optional playback progress as a percentage (0-100). When provided and > 2,
   * an orange bar is rendered along the bottom of the thumbnail. Use this for
   * "Continue Watching"-style surfaces.
   */
  playbackProgress?: number;
  /**
   * When provided, skips the client-side access check and uses this value directly.
   * Use this when unlock status has already been computed server-side (e.g. library page).
   */
  unlocked?: boolean;
}

/**
 * Thumbnail component for comedy special videos.
 * Displays the preview image with overlays for pricing, lock status, "New" badge, and runtime.
 *
 * @param special - The special/video data including pricing, runtime, and unlock status
 * @param displayFeatured - display the "Featured" badge?
 * @param comedianDisplayName - The comedian's display name for alt text
 * @param className - optional classname for styling
 */
export default function ComedianVideoThumbnail({
  special,
  displayFeatured = false,
  className,
  borderRadius = 6, // px
  withPlayIcon = false,
  playbackProgress,
  unlocked: unlockedProp,
}: ComedianVideoThumbnailProps) {
  const {
    title,
    runtime,
    requires_subscription,
    requires_purchase,
    preview_img,
    price,
  } = special;

  const user = useUser();
  const userId = user === undefined ? undefined : (user?.id ?? null);

  const { unlocked: hookUnlocked, isLoading: unlockLoading } =
    useSpecialUnlockedForUser({
      specialId: special.id,
      userId,
      special,
    });

  // Use the server-computed value when provided; fall back to the hook.
  const unlocked = unlockedProp !== undefined ? unlockedProp : hookUnlocked;
  const isLoading =
    user === undefined || (unlockedProp === undefined && unlockLoading);

  const fourteenDaysAgo = dayjs().subtract(14, "days");
  const specialPublishDate = dayjs(special.published_at);
  const isNew = specialPublishDate.isAfter(fourteenDaysAgo);

  const posterImg = preview_img
    ? getCDNImageURL(preview_img, "gravity=auto,fit=crop,height=236,width=411")
    : null;

  if (!posterImg) {
    return (
      <div className="overflow-hidden bg-gray-200 rounded-md animate-pulse aspect-video" />
    );
  }

  const getDisplayCtaButton = () => {
    // free specials don't need a CTA
    if (!requires_purchase && !requires_subscription) return false;

    // show nothing if we're loading
    if (isLoading) return false;

    // show the button if the user is not signed in
    if (!user) return true;

    // if the user is signed in, and:
    // has not "unlocked" the special (subscribed, purchased etc)
    if (!unlocked) return true;

    // otherwise show nothing
    return false;
  };

  const displayCtaButton = getDisplayCtaButton();

  return (
    <div
      className={cn("relative overflow-hidden aspect-video", className)}
      style={{ borderRadius }}
    >
      <Image
        src={posterImg}
        alt={title}
        fill
        placeholder="blur"
        blurDataURL={rgbDataURL(16, 16, 16)}
        className="object-cover object-top bg-neutral-300 transition-all duration-150 w-auto h-full z-10"
      />

      <div className="p-2 rounded-3xl bg-white flex gap-x-0.5">
        <span className="px-1 font-bold text-primary">
          {price > 0
            ? `$${price}`
            : !requires_subscription
              ? "Free!"
              : "Subscribe"}
        </span>
        <LockClosedIcon className="h-5 w-5 text-primary" />
      </div>

      {withPlayIcon && !displayCtaButton ? (
        <div className="absolute w-9 h-9 md:w-11 md:h-11 bg-black/90 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 rounded-full flex flex-row justify-center items-center">
          <Play fill="white" color="white" className="w-4 h-4 md:w-6 md:h-6" />
        </div>
      ) : (
        displayCtaButton && (
          <>
            <div className="absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-[#4A4A4A]/40 z-20" />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30">
              <button
                type="button"
                className="text-xs font-semibold text-white bg-primary flex flex-row items-center gap-x-2 px-3.5 py-2 rounded-lg drop-shadow-sm"
              >
                <LockIcon color="#ffffff" width={20} height={20} />
                <span className="whitespace-nowrap">
                  <WatchCTAButton special={special} />
                </span>
              </button>
            </div>
          </>
        )
      )}

      <div className="absolute top-2.5 left-2.5 z-30 flex flex-row gap-x-2 justify-start items-center">
        {/* for livestreams, render on the livestream badge */}
        {special.livestream ? (
          <ComedianVideoLivestreamBadge special={special} />
        ) : (
          <>
            {displayFeatured && (
              <div className="bg-[#FFF2E7] text-primary text-xs font-semibold flex flex-row gap-1 rounded-[4px] py-1 px-[5px] items-center">
                <span>Featured</span>
              </div>
            )}
            {isNew && (
              <div className="bg-[#FFF2E7] text-primary text-xs font-semibold flex flex-row gap-1 rounded-[4px] py-1 px-[5px] items-center">
                <span>New</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="absolute bottom-2.5 right-2.5 z-30 bg-[#1B1B1B]/80 text-white text-xs font-semibold flex flex-row gap-1 rounded-[4px] py-1 px-[5px] items-center">
        <span>{formatRuntime(runtime)}</span>
      </div>

      {playbackProgress !== undefined && playbackProgress > 2 && (
        <div
          role="progressbar"
          aria-valuenow={Math.round(playbackProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Playback progress"
          className="absolute bottom-0 left-0 right-0 h-[4px] bg-white/30 z-30"
        >
          <div
            className="h-full bg-primary"
            style={{ width: `${Math.min(100, playbackProgress)}%` }}
          />
        </div>
      )}
    </div>
  );
}
